import { Injectable, Injector } from '@angular/core';
import { BaseState } from 'src/app/common/base/base.state'; import { Action, Selector, State, StateContext } from '@ngxs/store';
import { CommentCreate, CommentDelete, CommentLoadBySerialNo, CommentRefresh, CommentRefreshByID, CommentUpdate } from './comments.action';
import { CommentResource } from 'src/app/controls/comments-and-attachments/services/comment';
import { CommentService } from 'src/app/controls/comments-and-attachments/services/comments.service';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';


export class CommentStateModel {
  data: CommentResource[] = [];
  // 6963: serial numbers whose comments are held in the store (forms opened this session)
  serialNos: string[] = [];
}

@State<CommentStateModel>({
  name: 'Comments',
  defaults: {
    data: [],
    serialNos: []
  }
})

@Injectable()
export class CommentState extends BaseState {
  constructor(
    protected override injector: Injector,
    private service: CommentService

  ) {
    super(injector);
  }

  @Selector()
  static read(state: CommentStateModel) {
    return state.data;
  }

  /**
   * Reloads the comments of every form already loaded in the store; forms never opened are not requested.
   * @param ctx NGXS state context used to dispatch the per-form reloads.
   */
  @Action(CommentRefresh)
  refresh({ getState, dispatch }: StateContext<CommentStateModel>) {
    // 6963: a full refresh no longer requests every comment in the system
    const serialNos = getState().serialNos;
    if (serialNos.length) {
      return dispatch(serialNos.map(serialNo => new CommentLoadBySerialNo(serialNo, true)));
    }
    return;
  }

  /**
   * Requests one form's comments and replaces that form's entries in the store. Skipped when the form is already loaded unless forced.
   * @param ctx NGXS state context.
   * @param action Serial number to load and whether to reload it when already present.
   * @returns The request observable, so the dispatch completes when the comments are in the store.
   */
  @Action(CommentLoadBySerialNo)
  loadBySerialNo({ getState, patchState }: StateContext<CommentStateModel>, { serialNo, force }: CommentLoadBySerialNo) {
    const loaded = getState().serialNos.includes(serialNo);
    if (!serialNo || (loaded && !force)) {
      return;
    }
    // Mark as loaded before the request so a second component opening the same form does not request it again
    if (!loaded) {
      patchState({ serialNos: [...getState().serialNos, serialNo] });
    }
    return this.service.ReadBySerialNo(serialNo).pipe(
      tap(items => {
        patchState({
          data: [...getState().data.filter(x => x.serialNo != serialNo), ...items]
        });
      }),
      catchError(error => {
        // Allow a later open of the form to retry the request
        patchState({ serialNos: getState().serialNos.filter(x => x != serialNo) });
        console.error(error);
        return of([]);
      })
    );
  }

  /**
   * Reloads one comment from the API, keeping it only when its form is loaded in the store.
   * @param ctx NGXS state context.
   * @param action ID of the comment to reload.
   */
  @Action(CommentRefreshByID)
  refreshByID({ getState, patchState }: StateContext<CommentStateModel>, { id }: CommentRefreshByID) {
    this.service.ReadOne(id).subscribe(
      item => {
        const state = getState();
        // 6963: ignore comments of forms that are not loaded, so the store only grows with opened forms
        if (!state.serialNos.includes(item.serialNo)) {
          return;
        }
        patchState({
          data: [...state.data.filter(x => x.id != id), item]
        });
      },
      error => console.error(error)
    );
  }

  /**
   * Adds a comment received through SignalR when its form is loaded in the store.
   * @param ctx NGXS state context.
   * @param action Created comment.
   */
  @Action(CommentCreate)
  create({ getState, patchState }: StateContext<CommentStateModel>, { item }: CommentCreate) {
    const state = getState();
    // 6963: only keep comments of loaded forms; replace by ID in case the comment is already present
    if (!state.serialNos.includes(item.serialNo)) {
      return;
    }
    patchState({
      data: [...state.data.filter(x => x.id != item.id), item]
    });
  }

  @Action(CommentUpdate)
  update({ getState, patchState }: StateContext<CommentStateModel>, { id, item }: CommentUpdate) {
    const state = getState();
    const index = state.data.map(x => x.id).indexOf(id);

    if (index >= 0) {
      state.data.splice(index, 1);
      patchState({
        data: [...state.data, item]
      });
    }
  }

  @Action(CommentDelete)
  delete({ getState, patchState }: StateContext<CommentStateModel>, { id }: CommentDelete) {
    patchState({
      data: getState().data.filter(x => x.id != id)
    });
  }

}
